
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

interface TipFormFieldsProps {
  title: string;
  content: string;
  emoji: string;
  votes?: number;
  status?: string;
  universityId: number | null;
  mode: 'add' | 'edit';
  onTitleChange: (value: string) => void;
  onContentChange: (value: string) => void;
  onEmojiChange: (value: string) => void;
  onVotesChange?: (value: number) => void;
  onStatusChange?: (value: string) => void;
  onUniversityChange: (value: number | null) => void;
}

interface University {
  id: number;
  name: string;
}

export function TipFormFields({
  title,
  content,
  emoji,
  votes,
  status,
  universityId,
  mode,
  onTitleChange,
  onContentChange,
  onEmojiChange,
  onVotesChange,
  onStatusChange,
  onUniversityChange,
}: TipFormFieldsProps) {
  const [universities, setUniversities] = useState<University[]>([]);

  useEffect(() => {
    const fetchUniversities = async () => {
      const { data, error } = await supabase
        .from('universities')
        .select('id, name')
        .order('name');
      
      if (!error && data) {
        setUniversities(data);
      }
    };

    fetchUniversities();
  }, []);

  return (
    <>
      <div className="space-y-2">
        <label htmlFor="title" className="text-sm font-medium">Title</label>
        <Input
          id="title"
          placeholder="Enter tip title..."
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="content" className="text-sm font-medium">Content</label>
        <Textarea
          id="content"
          placeholder="Enter tip content..."
          value={content}
          onChange={(e) => onContentChange(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="emoji" className="text-sm font-medium">Emoji</label>
        <Input
          id="emoji"
          placeholder="Enter emoji..."
          value={emoji}
          onChange={(e) => onEmojiChange(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="university" className="text-sm font-medium">University (Select "All Universities" for global tips)</label>
        <Select
          value={universityId?.toString() ?? "all"}
          onValueChange={(value) => onUniversityChange(value === "all" ? null : parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a university" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Universities</SelectItem>
            {universities.map((university) => (
              <SelectItem key={university.id} value={university.id.toString()}>
                {university.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {mode === 'edit' && (
        <>
          <div className="space-y-2">
            <label htmlFor="votes" className="text-sm font-medium">Votes</label>
            <Input
              id="votes"
              type="number"
              value={votes}
              onChange={(e) => onVotesChange?.(parseInt(e.target.value) || 0)}
              min={0}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="status" className="text-sm font-medium">Status</label>
            <Input
              id="status"
              value={status}
              onChange={(e) => onStatusChange?.(e.target.value)}
              required
            />
          </div>
        </>
      )}
    </>
  );
}
